import type { LucideIcon } from "lucide-react";
import { Brain, Target, TrendingUp, Zap } from "lucide-react";

export interface BlogCategory {
  id: "ai" | "marketing" | "automation" | "strategy";
  name: string;
  icon: LucideIcon;
}

export interface ArticleSection {
  heading: string;
  body: string[];
}

export interface ArticleHighlight {
  title: string;
  description: string;
}

export interface BlogPost {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  heroImage: string;
  image: string;
  categoryId: BlogCategory["id"];
  category: string;
  publishedAt: string;
  readTime: string;
  featured?: boolean;
  tags: string[];
  author: {
    name: string;
    role: string;
    avatar: string;
  };
  sections: ArticleSection[];
  highlights?: ArticleHighlight[];
  keyTakeaways: string[];
  quote?: {
    text: string;
    author: string;
    role: string;
  };
  resources?: {
    label: string;
    href: string;
  }[];
}

export const BLOG_CATEGORIES: BlogCategory[] = [
  {
    id: "ai",
    name: "AI & Machine Learning",
    icon: Brain,
  },
  {
    id: "marketing",
    name: "Marketing Tips",
    icon: TrendingUp,
  },
  {
    id: "automation",
    name: "Automation",
    icon: Zap,
  },
  {
    id: "strategy",
    name: "Strategy",
    icon: Target,
  },
];

export const BLOG_POSTS: BlogPost[] = [
  {
    id: 1,
    slug: "ai-powered-marketing-trends-2024",
    title: "The Future of AI-Powered Marketing: Trends to Watch in 2024",
    excerpt:
      "Discover the AI marketing bets shaping 2024, from predictive journeys to compliant content operations.",
    heroImage: "/images/blog/ai-future.jpg",
    image: "/images/blog/ai-future.jpg",
    categoryId: "ai",
    category: "AI & Machine Learning",
    publishedAt: "2024-12-15",
    readTime: "8 min read",
    featured: true,
    tags: ["ai", "automation", "strategy", "future"],
    author: {
      name: "Sarah Chen",
      role: "Marketing Manager",
      avatar: "/images/team/sarah.jpg",
    },
    sections: [
      {
        heading: "Why 2024 is a tipping point",
        body: [
          "Teams finally have the data infrastructure to plug AI into the customer journey instead of siloed experiments.",
          "Budgets are shifting from speculative pilots to owned models, which means marketing leaders must ship pragmatic wins fast.",
        ],
      },
      {
        heading: "Designing predictive customer journeys",
        body: [
          "Predictive scoring is no longer just for sales hand-offs. High-growth teams are using intent models to personalize onboarding, lifecycle messaging, and support content.",
          "A lightweight way to start is mapping your existing funnel milestones and attaching a single predictive signal to each stage.",
        ],
      },
      {
        heading: "Operational guardrails for ethical AI",
        body: [
          "Legal and trust teams want transparent decision logs. Documenting prompt libraries, model choices, and QA workflows keeps everyone aligned.",
          "Pair AI copilots with a human reviewer for regulated touchpoints (finance, health, security) and archive the review outcome for auditing.",
        ],
      },
      {
        heading: "How to ship your first AI win",
        body: [
          "Pick a single lifecycle moment (trial activation, churn recovery, onboarding) and measure the lift from AI-generated variations versus your control.",
          "Ship iteratively: launch a narrow use case, capture the delta, and reinvest savings into the next automation.",
        ],
      },
    ],
    highlights: [
      {
        title: "60%",
        description: "of CMOs rank predictive journeys as their #1 AI investment for 2024.",
      },
      {
        title: "4 weeks",
        description: "Average time it takes our customers to ship the first AI-assisted campaign.",
      },
      {
        title: "3x",
        description: "Increase in content throughput when paired with human review loops.",
      },
    ],
    keyTakeaways: [
      "Ship focused wins before scaling custom models.",
      "Document prompts, reviewers, and decisions for compliance.",
      "Pair predictive signals with lifecycle checkpoints.",
    ],
    quote: {
      text: "AI used to be experimental. In 2024 it is table stakes for orchestrating customer journeys at scale.",
      author: "Sarah Chen",
      role: "CEO, Blyrha",
    },
    resources: [
      {
        label: "Lifecycle experimentation worksheet",
        href: "https://blyrha.com/resources/lifecycle-experiments",
      },
      {
        label: "Prompt design checklist",
        href: "https://blyrha.com/resources/prompt-checklist",
      },
    ],
  },
  {
    id: 2,
    slug: "ai-content-marketing-strategy",
    title: "10 Ways AI Can Transform Your Content Marketing Strategy",
    excerpt:
      "Tactical workflows to boost editorial velocity without sacrificing brand voice.",
    heroImage: "/images/blog/content-strategy.jpg",
    image: "/images/blog/content-strategy.jpg",
    categoryId: "marketing",
    category: "Marketing Tips",
    publishedAt: "2024-12-10",
    readTime: "6 min read",
    tags: ["content", "workflow", "personalization"],
    author: {
      name: "Marcus Rodriguez",
      role: "Chief HR Officer",
      avatar: "/images/team/marcus.jpg",
    },
    sections: [
      {
        heading: "Editorial research copilots",
        body: [
          "Use AI to summarize subject-matter interviews and surface angle ideas while the team focuses on narrative.",
          "Feed transcripts, call notes, and competitive intel into a shared research space so every contributor starts with the same context.",
        ],
      },
      {
        heading: "Voice-safe drafting",
        body: [
          "Train lightweight style guides (tone, banned phrases, formatting) and apply them as guardrails around drafting prompts.",
          "Pair AI-generated outlines with human editors who enrich examples, quotes, and data references.",
        ],
      },
      {
        heading: "Multiformat atomization",
        body: [
          "Convert flagship pieces into scripts, social posts, and nurture drips in minutes by feeding structured briefs into templated prompts.",
          "Maintain a library of approved CTAs and product talking points so downstream assets stay accurate.",
        ],
      },
    ],
    highlights: [
      {
        title: "15 hrs",
        description: "Average editorial time reclaimed per campaign.",
      },
      {
        title: "92%",
        description: "Brand voice adherence when prompts reference the style guide.",
      },
    ],
    keyTakeaways: [
      "Treat AI as an acceleration layer, not auto-pilot.",
      "Centralize research inputs to avoid redundant digging.",
      "Codify voice rules to keep drafts on-brand.",
    ],
  },
  {
    id: 3,
    slug: "automation-vs-personalization",
    title: "Automation vs. Personalization: Finding the Perfect Balance",
    excerpt:
      "Learn when to automate, when to humanize, and how to blend both for delightful customer journeys.",
    heroImage: "/images/blog/automation-balance.jpg",
    image: "/images/blog/automation-balance.jpg",
    categoryId: "automation",
    category: "Automation",
    publishedAt: "2024-12-05",
    readTime: "5 min read",
    tags: ["automation", "personalization", "journeys"],
    author: {
      name: "Elena Kowalski",
      role: "Head of Product",
      avatar: "/images/team/elena.jpg",
    },
    sections: [
      {
        heading: "Map moments of truth",
        body: [
          "Identify the 3-10 customer interactions where a human touch has the highest ROI (pricing talk, security reviews, executive hand-offs).",
          "Automate the checklist work around those touchpoints so humans can focus on nuance.",
        ],
      },
      {
        heading: "Progressive personalization",
        body: [
          "Start with behavioral signals (pages viewed, product usage) before layering firmographic or intent data.",
          "Use AI to recommend the next-best action, then let reps approve, edit, or reject suggestions to keep control.",
        ],
      },
      {
        heading: "Feedback loops",
        body: [
          "Pipeline automation must feed learning back into your models. Tag every manual override with a reason so the system can adapt.",
          "Share qualitative notes with RevOps weekly to decide which journeys graduate from manual to automated.",
        ],
      },
    ],
    keyTakeaways: [
      "Design automation around human strengths, not replacements.",
      "Layer personalization progressively to protect data privacy.",
      "Instrument every manual override to keep models honest.",
    ],
  },
  {
    id: 4,
    slug: "data-driven-decision-making",
    title: "Data-Driven Decision Making: The Key to Marketing Success",
    excerpt:
      "Translate dashboards into decisive action with the right rituals, metrics, and accountability.",
    heroImage: "/images/blog/data-driven.jpg",
    image: "/images/blog/data-driven.jpg",
    categoryId: "strategy",
    category: "Strategy",
    publishedAt: "2024-11-28",
    readTime: "7 min read",
    tags: ["analytics", "dashboards", "planning"],
    author: {
      name: "Sarah Chen",
      role: "Marketing Manager",
      avatar: "/images/team/sarah.jpg",
    },
    sections: [
      {
        heading: "Instrument outcomes, not vanity",
        body: [
          "Choose metrics that prove behavioral change (activation, retention, upsell) instead of surface stats like opens or clicks.",
          "Align dashboards to the questions executives ask: Where are we leaking pipeline? Which channels create compounding value?",
        ],
      },
      {
        heading: "Decision rituals",
        body: [
          "Weekly growth reviews should end with a single owner, an action, and a due date. Otherwise analytics become entertainment.",
          "Adopt a traffic-light system so teams can spot when to lean in, hold, or pivot without rereading entire reports.",
        ],
      },
      {
        heading: "Operationalizing insights",
        body: [
          "Push key metrics into the tools your teams already live in (CRM, project management, product analytics).",
          "Automate alerts for threshold breaches so no one is waiting for the next meeting to respond.",
        ],
      },
    ],
    keyTakeaways: [
      "Metrics should answer a decision, not decorate a slide.",
      "Cadence beats volume: review fewer dashboards more often.",
      "Route insights directly into execution tools for fast action.",
    ],
    quote: {
      text: "Great dashboards inspire action because everyone knows what to do next.",
      author: "Elena Kowalski",
      role: "Head of Product",
    },
  },
  {
    id: 5,
    slug: "ai-marketing-funnels",
    title: "Building Effective Marketing Funnels with AI Insights",
    excerpt:
      "Combine funnel analytics with AI scoring to prioritize the experiments that unlock scale.",
    heroImage: "/images/blog/marketing-funnels.jpg",
    image: "/images/blog/marketing-funnels.jpg",
    categoryId: "ai",
    category: "AI & Machine Learning",
    publishedAt: "2024-11-20",
    readTime: "9 min read",
    tags: ["funnels", "experimentation", "ai"],
    author: {
      name: "Marcus Rodriguez",
      role: "Chief Operating Officer",
      avatar: "/images/team/marcus.jpg",
    },
    sections: [
      {
        heading: "Surface hidden drop-offs",
        body: [
          "Blend product telemetry with marketing touchpoints to see where qualified leads stall.",
          "Feed enriched datasets into clustering models to reveal segments that respond to different nudges.",
        ],
      },
      {
        heading: "Score experiments by impact",
        body: [
          "Use AI to simulate uplift ranges for each experiment so PMMs can prioritize confidently.",
          "Pair scoring with resource heatmaps (design, dev, paid spend) to understand true opportunity cost.",
        ],
      },
      {
        heading: "Close the loop",
        body: [
          "Push experiment outcomes back into the model so future prioritization reflects reality, not assumptions.",
          "Share a living changelog so sales, success, and exec teams know which levers moved the funnel.",
        ],
      },
    ],
    highlights: [
      {
        title: "38%",
        description: "Average lift in SQL conversion after deploying AI scoring.",
      },
      {
        title: "120 hrs",
        description: "Manual analysis time saved per quarter.",
      },
    ],
    keyTakeaways: [
      "Unify product and marketing data for true funnel visibility.",
      "Score experiments by impact *and* effort.",
      "Feed results back into the model to stay accurate.",
    ],
  },
  {
    id: 6,
    slug: "roi-optimization-ai-marketing",
    title: "ROI Optimization: Measuring What Matters in AI Marketing",
    excerpt:
      "Prioritize the metrics, cadences, and tooling that prove the business impact of your AI initiatives.",
    heroImage: "/images/blog/roi-optimization.jpg",
    image: "/images/blog/roi-optimization.jpg",
    categoryId: "strategy",
    category: "Strategy",
    publishedAt: "2024-11-15",
    readTime: "6 min read",
    tags: ["roi", "reporting", "governance"],
    author: {
      name: "Elena Kowalski",
      role: "Head of Product",
      avatar: "/images/team/elena.jpg",
    },
    sections: [
      {
        heading: "Pinpoint meaningful metrics",
        body: [
          "Start with the customer behavior you want to change—activation, expansion, retention—and map one metric per stage.",
          "Avoid vanity layers. If a metric does not trigger a decision or an action owner, it does not belong in your ROI model.",
        ],
      },
      {
        heading: "Instrument AI-attributed lift",
        body: [
          "Tag every experiment, campaign, and workflow that uses AI so finance can tie outcomes back to the investment.",
          "Pair qualitative notes with quantitative lift so executives understand the story behind the numbers.",
        ],
      },
      {
        heading: "Automate ROI rituals",
        body: [
          "Push ROI snapshots into the tools leaders already review weekly instead of adding another static deck.",
          "Automate variance alerts so teams can course-correct mid-quarter rather than during QBRs.",
        ],
      },
    ],
    highlights: [
      {
        title: "$3.4M",
        description: "Annual pipeline attributed to Blyrha customers' AI-assisted plays.",
      },
      {
        title: "45%",
        description: "Reduction in reporting time after automating ROI cadences.",
      },
    ],
    keyTakeaways: [
      "Tie every metric to a decision and an owner.",
      "Label AI-powered work so finance can measure lift.",
      "Automate variance alerts to protect in-quarter goals.",
    ],
    quote: {
      text: "ROI is a communication tool. When everyone agrees on the signals, funding follows.",
      author: "Elena Kowalski",
      role: "Head of Product",
    },
    resources: [
      {
        label: "ROI operating rhythm template",
        href: "https://blyrha.com/resources/roi-operating-rhythm",
      },
    ],
  },
];

export const getBlogPostBySlug = (slug: string) =>
  BLOG_POSTS.find((post) => post.slug === slug);

export const getRelatedPosts = (slug: string, limit = 3) => {
  const current = getBlogPostBySlug(slug);

  return BLOG_POSTS.filter((post) => post.slug !== slug)
    .map((post) => {
      const sharedTags = current
        ? post.tags.filter((tag) => current.tags.includes(tag)).length
        : 0;
      return {
        post,
        score: sharedTags,
      };
    })
    .sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }

      return (
        new Date(b.post.publishedAt).getTime() -
        new Date(a.post.publishedAt).getTime()
      );
    })
    .slice(0, limit)
    .map(({ post }) => post);
};

export const formatBlogDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
